import {useEffect, useState} from "react";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useGetPortalTeamQuery} from "../../../../store/generated/api";
import type {PurchaseFlow} from "../../../views/user/PricingView/pricingProductCatalog";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "../../../basic/dialog.tsx";
import {Button} from "../../../basic/button.tsx";
import {Input} from "../../../basic/input.tsx";

type CustomerType = "private" | "business";

interface PaymentMethodDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onCardPayment: (customerType: CustomerType, vatNumber?: string) => void;
    onInvoicePayment: () => void;
    title?: string;
    flow?: PurchaseFlow;
}

export function PaymentMethodDialog({
                                        open,
                                        onOpenChange,
                                        onCardPayment,
                                        onInvoicePayment,
                                        title,
                                        flow = "customer-type"
                                    }: PaymentMethodDialogProps) {
    const {t} = useTranslationWithKeys();
    const {data: teamData} = useGetPortalTeamQuery({page: 0, size: 1});
    const [selectedCustomerType, setSelectedCustomerType] = useState<CustomerType | null>(
        flow === "business" ? "business" : null
    );
    const [selectedBusinessPayment, setSelectedBusinessPayment] = useState<"card" | "invoice" | null>(null);
    const [vatNumber, setVatNumber] = useState("");

    // Pre-fill VAT from team settings when the card step is reached
    useEffect(() => {
        if (selectedBusinessPayment === "card" && !vatNumber) {
            setVatNumber(teamData?.team?.vatNumber ?? "");
        }
    }, [selectedBusinessPayment]);

    const resetState = () => {
        setSelectedCustomerType(null);
        setSelectedBusinessPayment(null);
        setVatNumber("");
    };

    const handleDialogChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            resetState();
        }
        onOpenChange(nextOpen);
    };

    const handlePrivateSelection = () => {
        handleDialogChange(false);
        onCardPayment("private");
    };

    const handleBusinessCardPayment = () => {
        if (!vatNumber.trim()) {
            return;
        }
        handleDialogChange(false);
        onCardPayment("business", vatNumber.trim());
    };

    const handleBusinessInvoicePayment = () => {
        handleDialogChange(false);
        onInvoicePayment();
    };

    return (
        <Dialog open={open} onOpenChange={handleDialogChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{title || t('payment.customerType.title')}</DialogTitle>
                    <DialogDescription>
                        {selectedCustomerType === "business" ? t('payment.method.description') : t('payment.customerType.description')}
                    </DialogDescription>
                </DialogHeader>

                {selectedCustomerType === null && (
                    <div className="flex flex-col gap-3">
                        <Button
                            onClick={handlePrivateSelection}
                            className="w-full h-auto py-4 flex-col gap-2"
                            variant="default"
                        >
                            <span className="text-lg font-semibold">{t('payment.customerType.privateTitle')}</span>
                            <span className="text-sm opacity-80">{t('payment.customerType.privateDescription')}</span>
                        </Button>
                        <Button
                            onClick={() => setSelectedCustomerType("business")}
                            className="w-full h-auto py-4 flex-col gap-2"
                            variant="outline"
                        >
                            <span className="text-lg font-semibold">{t('payment.customerType.businessTitle')}</span>
                            <span className="text-sm opacity-80">{t('payment.customerType.businessDescription')}</span>
                        </Button>
                    </div>
                )}

                {selectedCustomerType === "business" && selectedBusinessPayment === null && (
                    <div className="flex flex-col gap-3">
                        <Button
                            onClick={() => setSelectedBusinessPayment("card")}
                            className="w-full h-auto py-4 flex-col gap-2"
                            variant="default"
                        >
                            <span className="text-lg font-semibold">{t('payment.method.cardPayment')}</span>
                            <span className="text-sm opacity-80">{t('payment.method.cardPaymentDescription')}</span>
                        </Button>
                        <Button
                            onClick={handleBusinessInvoicePayment}
                            className="w-full h-auto py-4 flex-col gap-2"
                            variant="outline"
                        >
                            <span className="text-lg font-semibold">{t('payment.method.invoicePayment')}</span>
                            <span className="text-sm opacity-80">{t('payment.method.invoicePaymentDescription')}</span>
                        </Button>
                        <Button variant="ghost" onClick={() => setSelectedCustomerType(null)}
                                className={flow === "business" ? "hidden" : undefined}>
                            {t('common.action.previous')}
                        </Button>
                    </div>
                )}

                {selectedCustomerType === "business" && selectedBusinessPayment === "card" && (
                    <div className="flex flex-col gap-3">
                        <label htmlFor="business-vat" className="text-sm font-medium">
                            {t('settings.vatNumber')} *
                        </label>
                        <Input
                            id="business-vat"
                            value={vatNumber}
                            onChange={(event) => setVatNumber(event.target.value)}
                            placeholder={t('payment.vat.placeholder')}
                        />
                        <div className="flex gap-2 justify-end">
                            <Button variant="ghost" onClick={() => setSelectedBusinessPayment(null)}>
                                {t('common.action.previous')}
                            </Button>
                            <Button onClick={handleBusinessCardPayment} disabled={!vatNumber.trim()}>
                                {t('payment.vat.continueToCard')}
                            </Button>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={() => handleDialogChange(false)}>
                        {t('common.cancel')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
